import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Play, Image as ImageIcon, Video, Filter } from "lucide-react";
import g1 from "../assets/image/g1.jpg";
import g2 from "../assets/image/g2.jpg";
import gvideo from "../assets/image/gvideo.mp4";
import VideoModal from "../components/common/VideoModal";

const Gallery = () => {
  const [activeTab, setActiveTab] = useState("images");
  const [activeFilter, setActiveFilter] = useState("all");
  const videoRefs = useRef({});
  const [modalVideo, setModalVideo] = useState(null);

  const filters = [
    { id: "all", label: "All" },
    { id: "commercial", label: "Commercial" },
    { id: "residential", label: "Residential" },
    { id: "industrial", label: "Industrial" },
  ];

  const images = [
    {
      id: 1,
      category: "commercial",
      image: g1,
      title: "Office Space Cleaning",
      description: "Professional office cleaning service",
    },
    {
      id: 2,
      category: "residential",
      image: g2,
      title: "Home Deep Clean",
      description: "Complete home cleaning solution",
    },
    // Add more images...
  ];

  const videos = [
    {
      id: 1,
      category: "commercial",
      video: gvideo,
      title: "Commercial Cleaning Process",
      description: "Watch our cleaning process in action",
    },
    {
      id: 2,
      category: "residential",
      video: gvideo,
      title: "Residential Deep Clean",
      description: "Home cleaning transformation",
    },
  ];

  const handleVideoClick = (video) => {
    setModalVideo({
      src: video.youtubeUrl || video.video,
      isYoutube: !!video.youtubeUrl,
    });
  };

  const handleMouseEnter = (id) => {
    const video = videoRefs.current[id];
    if (video) {
      try {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            console.log("Playback prevented");
          });
        }
      } catch (error) {
        console.log("Play error:", error);
      }
    }
  };

  const handleMouseLeave = (id) => {
    const video = videoRefs.current[id];
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  const filteredContent = (contentType) => {
    const content = contentType === "images" ? images : videos;
    return activeFilter === "all"
      ? content
      : content.filter((item) => item.category === activeFilter);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-primary">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
              Our Project Gallery
            </h1>
            <p className="mt-4 text-lg text-white/90 max-w-2xl mx-auto">
              Explore our cleaning projects through images and videos
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Controls */}
      <section className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center py-6 space-y-4 sm:space-y-0">
            {/* Tab Selector */}
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab("images")}
                className={`flex items-center px-4 py-2 rounded-md transition-all ${
                  activeTab === "images"
                    ? "bg-white shadow text-primary"
                    : "text-gray-600 hover:text-primary"
                }`}
              >
                <ImageIcon className="w-5 h-5 mr-2" />
                Images
              </button>
              <button
                onClick={() => setActiveTab("videos")}
                className={`flex items-center px-4 py-2 rounded-md transition-all ${
                  activeTab === "videos"
                    ? "bg-white shadow text-primary"
                    : "text-gray-600 hover:text-primary"
                }`}
              >
                <Video className="w-5 h-5 mr-2" />
                Videos
              </button>
            </div>

            {/* Filter Dropdown */}
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="bg-white border border-gray-300 rounded-md py-2 px-4 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {filters.map((filter) => (
                  <option key={filter.id} value={filter.id}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Images Grid */}
          {activeTab === "images" && (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredContent("images").map((item) => (
                <motion.div
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={item.id}
                  className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-gray-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Videos Grid */}
          {activeTab === "videos" && (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredContent("videos").map((item) => (
                <motion.div
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={item.id}
                  className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                  onMouseEnter={() => handleMouseEnter(item.id)}
                  onMouseLeave={() => handleMouseLeave(item.id)}
                  onClick={() => handleVideoClick(item)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                    <video
                      ref={(el) => (videoRefs.current[item.id] = el)}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      loop
                      preload="auto" // Add this
                    >
                      <source src={gvideo} type="video/mp4" />{" "}
                      {/* Use imported video directly */}
                      Your browser does not support video playback.
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:opacity-0 transition-opacity duration-300">
                      <Play className="w-16 h-16 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-gray-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* No Results Message */}
          {filteredContent(activeTab).length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                No {activeTab} found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      <VideoModal
        isOpen={!!modalVideo}
        onClose={() => setModalVideo(null)}
        videoSrc={modalVideo?.src}
        isYoutube={modalVideo?.isYoutube}
      />
    </div>
  );
};

export default Gallery;
